<script setup lang="ts">

import type { UnreadHintType } from "@/api/unreadHint/types";
import CardCollectionHeaderFrame from "@/application/components/CardCollectionHeaderFrame.vue";
import BulkRemoveUnreadHints from "@/application/components/BulkRemoveUnreadHints.vue";
import AppButton from '@/application/components/ui/AppButton.vue';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { useUnreadHintStore } from "@/application/store/unreadHintStore";
import { ElInput } from 'element-plus';

type Props = {
  modelValue: string,
  unreadHintsCount: number,
  unreadHintsType: UnreadHintType
  unreadHintsLabel: string
  searchEnabled: boolean
  searchPlaceholder: string
  createNewButtonLabel: string
  showContextInfo: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void,
  (e: 'openCreateModal'): void,
  (e: 'searchFocus'): void,
  (e: 'searchBlur'): void,
}>()

const { removeUnreadHints } = useUnreadHintStore();
</script>

<template>
  <CardCollectionHeaderFrame>
    <div v-if="showContextInfo" class="hidden lg:block">
      <slot name="left" />
    </div>
    <div
      class="flex grow justify-end items-center space-x-2"
      :class="{'col-span-2': !showContextInfo, 'col-span-1': showContextInfo}"
    >
      <BulkRemoveUnreadHints
        v-if="unreadHintsCount > 0"
        :items="[unreadHintsLabel]"
        @select="removeUnreadHints([{ type: unreadHintsType }])"
      />
      <div class="w-full lg:w-80">
        <!-- Das Feld stand als handgeschriebenes `<input>` neben der Schaltfläche und blieb
             deshalb bei der Ablösung liegen — sie hat nur Ds-Komponenten angefasst. Aus den
             40px des Eigenbaus und den 44px der Skala wurden zwei verschiedene Höhen in
             derselben Zeile. `size="default"` steht ausdrücklich hier: die Zeile ist eine
             Werkzeugleiste, in der das Feld die Höhe der Schaltflächen daneben hält
             (`AppButton` steht fest auf `default`), kein Formular, dessen Felder mobil auf
             `large` gehen. -->
        <ElInput
          size="default"
          :model-value="modelValue"
          :disabled="!searchEnabled"
          :placeholder="searchPlaceholder"
          @update:model-value="(value: string) => emit('update:modelValue', value)"
          @focus="() => emit('searchFocus')"
          @blur="() => emit('searchBlur')"
        />
      </div>
      <slot name="filter" />
      <AppButton
        type="primary"
        class="hidden md:block"
        @click="emit('openCreateModal')"
      >
        {{ createNewButtonLabel }}
        <FaIcon name="plus" class="ml-1.5" />
      </AppButton>
    </div>
  </CardCollectionHeaderFrame>
</template>
